import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Brutalne scroll animacije optimizovane za performance
 * Koristi Intersection Observer i hardware acceleration za glatke animacije
 */

// Opcije za različite tipove animacija
const ANIMATION_TYPES = {
  fadeInUp: {
    initial: { opacity: 0, transform: 'translate3d(0, 40px, 0)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    transition: 'opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },
  fadeInLeft: {
    initial: { opacity: 0, transform: 'translate3d(-60px, 0, 0)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    transition: 'opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },
  fadeInRight: {
    initial: { opacity: 0, transform: 'translate3d(60px, 0, 0)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    transition: 'opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },
  scaleIn: {
    initial: { opacity: 0, transform: 'translate3d(0, 0, 0) scale3d(0.8, 0.8, 1)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)' },
    transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
  },
  slideInUp: {
    initial: { opacity: 0, transform: 'translate3d(0, 100%, 0)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    transition: 'opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  },
  flipInX: {
    initial: { opacity: 0, transform: 'perspective(400px) rotate3d(1, 0, 0, 90deg)' },
    animate: { opacity: 1, transform: 'perspective(400px) rotate3d(1, 0, 0, 0deg)' },
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  },
  bounceIn: {
    initial: { opacity: 0, transform: 'translate3d(0, 0, 0) scale3d(0.3, 0.3, 1)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)' },
    transition: 'opacity 0.6s cubic-bezier(0.215, 0.61, 0.355, 1), transform 0.6s cubic-bezier(0.215, 0.61, 0.355, 1)'
  },
  rotateIn: {
    initial: { opacity: 0, transform: 'translate3d(0, 0, 0) rotate3d(0, 0, 1, -180deg)' },
    animate: { opacity: 1, transform: 'translate3d(0, 0, 0) rotate3d(0, 0, 1, 0deg)' },
    transition: 'opacity 0.7s ease-out, transform 0.7s ease-out'
  }
};

/**
 * Hook za brutalne scroll animacije optimizovan za CSS klase
 */
export const useScrollAnimation = (animationType = 'fadeInUp', threshold = 0.2, delay = 0) => {
  const elementRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Mapiranje animacija na CSS klase
  const animationClassMap = {
    fadeInUp: 'fade-in-up',
    fadeInLeft: 'fade-in-left', 
    fadeInRight: 'fade-in-right',
    scaleIn: 'scale-in',
    slideInUp: 'slide-in-up',
    flipInX: 'flip-in-x',
    bounceIn: 'bounce-in',
    rotateIn: 'rotate-in'
  };

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Dodaj CSS klasu za početno stanje animacije
    const cssClass = animationClassMap[animationType] || 'fade-in-up';
    element.classList.add(cssClass);
    
    // Dodaj hardware acceleration
    element.style.willChange = 'opacity, transform';
    element.style.backfaceVisibility = 'hidden';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          
          // Dodaj delay i aktiviraj animaciju
          setTimeout(() => {
            element.classList.add('animate');
            setHasAnimated(true);
            
            // Ukloni will-change i cleanup nakon animacije
            setTimeout(() => {
              if (element) {
                element.style.willChange = 'auto';
                element.style.backfaceVisibility = 'visible';
              }
            }, 1200);
          }, delay);
        }
      },
      { threshold, rootMargin: '50px 0px -10% 0px' }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (element) {
        element.style.willChange = 'auto';
      }
    };
  }, [animationType, threshold, delay, hasAnimated, animationClassMap]);

  return { elementRef, isVisible, hasAnimated };
};

/**
 * Hook za staggered animacije (elementi se pojavljuju jedan za drugim)
 */
export const useStaggeredAnimation = (animationType = 'fadeInUp', staggerDelay = 150) => {
  const containerRef = useRef();
  const [visibleItems, setVisibleItems] = useState(new Set());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const items = container.querySelectorAll('.stagger-item');
    const animation = ANIMATION_TYPES[animationType];

    // Setup početnog stanja za sve elemente
    items.forEach(item => {
      Object.assign(item.style, {
        ...animation.initial,
        transition: animation.transition,
        willChange: 'opacity, transform',
        backfaceVisibility: 'hidden'
      });
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          items.forEach((item, index) => {
            if (!visibleItems.has(index)) {
              setTimeout(() => {
                Object.assign(item.style, animation.animate);
                setVisibleItems(prev => new Set([...prev, index]));
                
                // Ukloni will-change nakon animacije
                setTimeout(() => {
                  item.style.willChange = 'auto';
                }, 1000);
              }, index * staggerDelay);
            }
          });
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [animationType, staggerDelay, visibleItems]);

  return containerRef;
};

/**
 * Hook za parallax scroll efekat
 */
export const useParallaxScroll = (speed = 0.5, direction = 'vertical') => {
  const elementRef = useRef();
  const rafId = useRef();
  const lastScrollY = useRef(0);

  const updateParallax = useCallback(() => {
    if (!elementRef.current) return;

    const scrollY = window.pageYOffset;
    const deltaY = scrollY - lastScrollY.current;
    
    // Optimizacija - update samo ako je scroll značajan
    if (Math.abs(deltaY) < 1) {
      rafId.current = requestAnimationFrame(updateParallax);
      return;
    }

    const rect = elementRef.current.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isVisible) {
      const offset = (scrollY - rect.top) * speed;
      
      if (direction === 'vertical') {
        elementRef.current.style.transform = `translate3d(0, ${offset}px, 0)`;
      } else {
        elementRef.current.style.transform = `translate3d(${offset}px, 0, 0)`;
      }
    }

    lastScrollY.current = scrollY;
    rafId.current = requestAnimationFrame(updateParallax);
  }, [speed, direction]);

  useEffect(() => {
    const element = elementRef.current;
    if (element) {
      element.style.willChange = 'transform';
      element.style.backfaceVisibility = 'hidden';
    }

    rafId.current = requestAnimationFrame(updateParallax);

    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (element) {
        element.style.willChange = 'auto';
      }
    };
  }, [updateParallax]);

  return elementRef;
};

/**
 * Hook za reveal animacije sa custom timing funkcijama
 */
export const useRevealAnimation = (options = {}) => {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -100px 0px',
    animationClass = 'reveal-animate',
    duration = 800,
    easing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    transform = 'translateY(40px)',
    once = true
  } = options;

  const elementRef = useRef();
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Setup početnog stanja
    Object.assign(element.style, {
      opacity: '0',
      transform: transform,
      transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
      willChange: 'opacity, transform',
      backfaceVisibility: 'hidden'
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && (!once || !isRevealed)) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0px) translateZ(0)';
          element.classList.add(animationClass);
          setIsRevealed(true);
          
          // Cleanup nakon animacije
          setTimeout(() => {
            element.style.willChange = 'auto';
          }, duration + 100);
          
          if (once) {
            observer.disconnect();
          }
        } else if (!entry.isIntersecting && !once) {
          element.style.opacity = '0';
          element.style.transform = transform;
          element.classList.remove(animationClass);
          setIsRevealed(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, animationClass, duration, easing, transform, once, isRevealed]);

  return { elementRef, isRevealed };
};

/**
 * Hook za counter animacije
 */
export const useCounterAnimation = (endValue, duration = 2000, startValue = 0) => {
  const [count, setCount] = useState(startValue);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          
          const startTime = Date.now();
          const startNum = startValue;
          const endNum = endValue;
          
          const updateCounter = () => {
            const now = Date.now();
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function za smooth animaciju
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(startNum + (endNum - startNum) * easeOutQuart);
            
            setCount(currentValue);
            
            if (progress < 1) {
              requestAnimationFrame(updateCounter);
            }
          };
          
          requestAnimationFrame(updateCounter);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [endValue, duration, startValue, isVisible]);

  return { elementRef, count };
};

/**
 * Component wrapper za laku upotrebu scroll animacija
 */
export const ScrollAnimateWrapper = ({ 
  children, 
  animation = 'fadeInUp', 
  delay = 0, 
  threshold = 0.2,
  className = '',
  ...props 
}) => {
  const { elementRef } = useScrollAnimation(animation, threshold, delay);
  
  return (
    <div ref={elementRef} className={className} {...props}>
      {children}
    </div>
  );
};

/**
 * Component za staggered animacije
 */
export const StaggerContainer = ({ 
  children, 
  animation = 'fadeInUp', 
  staggerDelay = 150,
  className = '',
  ...props 
}) => {
  const containerRef = useStaggeredAnimation(animation, staggerDelay);
  
  return (
    <div ref={containerRef} className={className} {...props}>
      {children}
    </div>
  );
};

const scrollAnimationsExport = {
  useScrollAnimation,
  useStaggeredAnimation,
  useParallaxScroll,
  useRevealAnimation,
  useCounterAnimation,
  ScrollAnimateWrapper,
  StaggerContainer,
  ANIMATION_TYPES
};

export default scrollAnimationsExport;